import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { canEdit, canDelete, canEditModule } from "@/utils/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Car, Monitor, Wrench, Armchair, Plus, Edit, Trash2, Search,
  FileText, Building, User, FileDown, History, Package,
} from "lucide-react";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";

const CLASSIFICATIONS = [
  { value: "VEH", label: "Vehículos", icon: Car, color: "bg-blue-500" },
  { value: "CPT", label: "Cómputo", icon: Monitor, color: "bg-purple-500" },
  { value: "HER", label: "Herramientas", icon: Wrench, color: "bg-orange-500" },
  { value: "MUE", label: "Muebles", icon: Armchair, color: "bg-green-500" },
];

const ENTIDADES = ["CDMX", "EDOMEX", "MOR", "JAL", "NL", "QRO", "PUE", "GTO"];
const ESTATUS_OPTIONS = ["propio", "leasing"];
const UBICACIONES = ["Oficina", "Home Office", "Bodega", "En tránsito"];

const DEFAULT_POLITICAS = `En caso de pérdida por robo el trabajador deberá levantar el acta correspondiente ante el Ministerio Público, así mismo el trabajador y la Empresa tendrá que cubrir el monto deducible que determine el seguro.
NOTA: El seguro empresarial aplica únicamente en equipo de cómputo, omite la cobertura en accesorios, en ambos casos NO SE CUBRIRÁ LA PÉRDIDA POR EXTRAVÍO, por lo cual el empleado deberá cubrir en su totalidad el costo de estos.
La empresa se reserva el derecho de inspeccionar, retirar, dar mantenimiento, modificar, el equipo en sus configuraciones de hardware y software como considere apropiado.
En el caso que el trabajador deje de laborar para PD TRAVEL MEXICO, S.A de C.V: El empleado deberá regresar el equipo en las mismas condiciones como se le entregó en un inicio, en caso de tener desperfectos al momento de la entrega, se evaluarán los daños y se descontarán al empleado, la empresa conservará el equipo.
Al momento de la entrega se declara que el equipo se encuentra en óptimas condiciones por lo que el receptor asume la responsabilidad y el cuidado de dicho equipo y se compromete a utilizarlo con un fin estrictamente laboral.
No se podrán instalar programas ajenos de los que vienen instalados por defecto sin previa autorización del jefe directo.
Cualquier problema o fallo deberá ser informado al departamento a cargo quienes serán los encargados de solucionarlo o renovar el material según se requiera.`;

const FixedAssets = () => {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClassification, setFilterClassification] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [showHistorial, setShowHistorial] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [historialAsset, setHistorialAsset] = useState(null);

  const [formData, setFormData] = useState({
    clasificacion: "CPT", descripcion: "", marca: "", modelo: "", tipo_vehiculo: "", serie_vin: "",
    fecha_compra: "", valor_factura: "", proveedor: "", estatus: "propio",
    placas: "", entidad: "", vigencia_seguro: "", fecha_verificacion: "",
    tenencia_pagada: false, tenencia_year: new Date().getFullYear().toString(),
    tipo_resguardo: "", responsable_id: "", responsable_nombre: "", ubicacion_resguardo: "",
    politicas_asignacion: DEFAULT_POLITICAS,
  });

  useEffect(() => {
    fetchAssets();
    fetchWorkCenters();
    fetchEmployees();
    fetchProveedores();
  }, [filterClassification]);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const params = filterClassification !== "all" ? `?clasificacion=${filterClassification}` : "";
      const res = await axios.get(`${API}/assets${params}`, { withCredentials: true });
      setAssets(res.data);
    } catch (error) {
      if (error.response?.status !== 403) toast.error("Error al cargar activos");
    } finally { setLoading(false); }
  };

  const fetchWorkCenters = async () => {
    try { const res = await axios.get(`${API}/work-centers`, { withCredentials: true }); setWorkCenters(res.data); }
    catch (err) { console.error("Error fetching work centers:", err); }
  };

  const fetchEmployees = async () => {
    try { const res = await axios.get(`${API}/employees`, { withCredentials: true }); setEmployees(res.data); }
    catch (err) { console.error("Error fetching employees:", err); }
  };

  const fetchProveedores = async () => {
    try { const res = await axios.get(`${API}/proveedores-gasto?categoria=automotriz`, { withCredentials: true }); setProveedores(res.data); }
    catch (err) { console.error("Error fetching proveedores:", err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let submitData = { ...formData };
      if (formData.tipo_resguardo === "PF") {
        const emp = employees.find(e => e.id === formData.responsable_id);
        submitData.responsable_nombre = emp?.nombre || "";
      } else if (formData.tipo_resguardo === "PM") {
        const center = workCenters.find(c => c.id === formData.responsable_id);
        submitData.responsable_nombre = center?.nombre || "";
      }
      if (editingAsset) {
        await axios.put(`${API}/assets/${editingAsset.id}`, submitData, { withCredentials: true });
        toast.success("Activo actualizado");
      } else {
        await axios.post(`${API}/assets`, submitData, { withCredentials: true });
        toast.success("Activo creado");
      }
      setShowDialog(false);
      setEditingAsset(null);
      resetForm();
      fetchAssets();
    } catch (error) { toast.error(error.response?.data?.detail || "Error al guardar"); }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      clasificacion: asset.clasificacion, descripcion: asset.descripcion || "",
      marca: asset.marca || "", modelo: asset.modelo || "", tipo_vehiculo: asset.tipo_vehiculo || "", serie_vin: asset.serie_vin || "",
      fecha_compra: asset.fecha_compra || "", valor_factura: asset.valor_factura ?? "",
      proveedor: asset.proveedor || "", estatus: asset.estatus || "propio",
      placas: asset.placas || "", entidad: asset.entidad || "",
      vigencia_seguro: asset.vigencia_seguro || "", fecha_verificacion: asset.fecha_verificacion || "",
      tenencia_pagada: asset.tenencia_pagada || false,
      tenencia_year: asset.tenencia_year || new Date().getFullYear().toString(),
      tipo_resguardo: asset.tipo_resguardo || "", responsable_id: asset.responsable_id || "",
      responsable_nombre: asset.responsable_nombre || "", ubicacion_resguardo: asset.ubicacion_resguardo || "",
      politicas_asignacion: asset.politicas_asignacion || DEFAULT_POLITICAS,
    });
    setShowDialog(true);
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm("¿Eliminar este activo?")) return;
    try {
      await axios.delete(`${API}/assets/${assetId}`, { withCredentials: true });
      toast.success("Activo eliminado");
      fetchAssets();
    } catch (err) { console.error("Error deleting asset:", err); toast.error("Error al eliminar"); }
  };

  const handleDownloadActa = async (assetId, tipo) => {
    try {
      const endpoint = tipo === "asignacion" ? "acta-asignacion" : "acta-recepcion";
      const res = await axios.get(`${API}/assets/${assetId}/${endpoint}`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const asset = assets.find(a => a.id === assetId);
      const clave = asset?.clave || "activo";
      a.download = tipo === "asignacion" ? `Acta_Asignacion_${clave}.pdf` : `Acta_Recepcion_${clave}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Acta de ${tipo === "asignacion" ? "Asignación" : "Recepción"} descargada`);
      // Refresh history if open
      if (showHistorial && historialAsset?.id === assetId) {
        fetchHistorial(assetId);
      }
    } catch (err) {
      // La petición usa responseType=blob, así que en caso de error
      // `err.response.data` es un Blob (no JSON). Lo leemos y parseamos
      // para mostrar el mensaje real del backend en el toast.
      let msg = "Error al generar acta";
      try {
        const blob = err?.response?.data;
        if (blob instanceof Blob) {
          const txt = await blob.text();
          try {
            const j = JSON.parse(txt);
            msg = j.detail || j.message || txt || msg;
          } catch {
            if (txt) msg = txt;
          }
        } else if (typeof err?.response?.data === "string") {
          msg = err.response.data;
        } else if (err?.response?.data?.detail) {
          msg = err.response.data.detail;
        } else if (err?.message) {
          msg = err.message;
        }
      } catch { /* keep default */ }
      const status = err?.response?.status ? ` (HTTP ${err.response.status})` : "";
      toast.error(`${msg}${status}`);
      console.error("[acta] Error:", err?.response?.status, msg, err);
    }
  };

  const fetchHistorial = async (assetId) => {
    try {
      const res = await axios.get(`${API}/assets/${assetId}/historial`, { withCredentials: true });
      setHistorial(res.data);
    } catch (err) { console.error("Error fetching historial:", err); setHistorial([]); }
  };

  const handleShowHistorial = (asset) => {
    setHistorialAsset(asset);
    fetchHistorial(asset.id);
    setShowHistorial(true);
  };

  const resetForm = () => {
    setFormData({
      clasificacion: "CPT", descripcion: "", marca: "", modelo: "", tipo_vehiculo: "", serie_vin: "",
      fecha_compra: "", valor_factura: "", proveedor: "", estatus: "propio",
      placas: "", entidad: "", vigencia_seguro: "", fecha_verificacion: "",
      tenencia_pagada: false, tenencia_year: new Date().getFullYear().toString(),
      tipo_resguardo: "", responsable_id: "", responsable_nombre: "", ubicacion_resguardo: "",
      politicas_asignacion: DEFAULT_POLITICAS,
    });
  };

  const openNewDialog = () => { setEditingAsset(null); resetForm(); setShowDialog(true); };
  const getClassificationInfo = (code) => CLASSIFICATIONS.find(c => c.value === code) || CLASSIFICATIONS[0];

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getExpirationBadge = (dateStr) => {
    const days = getDaysUntil(dateStr);
    if (days === null) return null;
    if (days < 0) return <Badge className="bg-red-500">Vencido</Badge>;
    if (days <= 15) return <Badge className="bg-yellow-500">{days} días</Badge>;
    if (days <= 30) return <Badge className="bg-yellow-400">{days} días</Badge>;
    return <Badge className="bg-green-500">{days} días</Badge>;
  };

  const filteredAssets = assets.filter(asset =>
    asset.clave?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.marca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    asset.placas?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalAssets = assets.length;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="fixed-assets-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <ModuleHeaderIcon icon={Package} />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Activos Fijos (CARPEX)</h1>
              <p className="text-slate-500">Control patrimonial y resguardos</p>
            </div>
          </div>
          <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="new-asset-btn" disabled={!canEditModule(user, "activos_fijos")}>
            <Plus className="w-4 h-4 mr-2" /> Nuevo Activo
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg"><FileText className="w-5 h-5 text-slate-600" /></div>
                <div><p className="text-2xl font-bold">{totalAssets}</p><p className="text-xs text-slate-500">Total Activos</p></div>
              </div>
            </CardContent>
          </Card>
          {CLASSIFICATIONS.map(c => (
            <Card key={c.value}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${c.color} bg-opacity-20 rounded-lg`}><c.icon className={`w-5 h-5 ${c.color.replace("bg-", "text-")}`} /></div>
                  <div><p className="text-2xl font-bold">{assets.filter(a => a.clasificacion === c.value).length}</p><p className="text-xs text-slate-500">{c.label}</p></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Buscar por clave, descripción, marca o placas..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                </div>
              </div>
              <Select value={filterClassification} onValueChange={setFilterClassification}>
                <SelectTrigger className="w-[200px]"><SelectValue placeholder="Filtrar por tipo" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los tipos</SelectItem>
                  {CLASSIFICATIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>CLAVE</TableHead>
                  <TableHead>TIPO</TableHead>
                  <TableHead>DESCRIPCIÓN</TableHead>
                  <TableHead>MARCA/MODELO</TableHead>
                  <TableHead>SERIE/VIN</TableHead>
                  <TableHead>ESTATUS</TableHead>
                  <TableHead>RESGUARDO</TableHead>
                  <TableHead>VENCIMIENTOS</TableHead>
                  <TableHead className="text-right">ACCIONES</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10">Cargando...</TableCell></TableRow>
                ) : filteredAssets.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500">No hay activos</TableCell></TableRow>
                ) : (
                  filteredAssets.map((asset) => {
                    const classInfo = getClassificationInfo(asset.clasificacion);
                    return (
                      <TableRow key={asset.id} className="[&>td]:py-1.5 [&>td]:whitespace-nowrap">
                        <TableCell className="font-mono font-semibold text-xs">{asset.clave}</TableCell>
                        <TableCell><Badge className={`${classInfo.color} text-white text-[10px] px-1.5 py-0`}>{classInfo.label}</Badge></TableCell>
                        <TableCell className="font-medium text-sm max-w-[220px] truncate" title={asset.descripcion}>{asset.descripcion}</TableCell>
                        <TableCell className="text-sm max-w-[220px] truncate" title={`${asset.marca || ""} ${asset.modelo || ""}`}>{asset.marca} {asset.modelo}</TableCell>
                        <TableCell className="font-mono text-xs">{asset.serie_vin}</TableCell>
                        <TableCell><Badge variant={asset.estatus === "propio" ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">{asset.estatus}</Badge></TableCell>
                        <TableCell className="max-w-[200px] truncate" title={asset.responsable_nombre || "Sin asignar"}>
                          {asset.responsable_nombre ? (
                            <span className="inline-flex items-center gap-1 text-xs">
                              {asset.tipo_resguardo === "PF" ? <User className="w-3 h-3 shrink-0" /> : <Building className="w-3 h-3 shrink-0" />}
                              <span className="truncate">{asset.responsable_nombre}</span>
                            </span>
                          ) : <span className="text-slate-400 text-xs">Sin asignar</span>}
                        </TableCell>
                        <TableCell>
                          {asset.clasificacion === "VEH" && (
                            <div className="flex items-center gap-2">
                              {asset.vigencia_seguro && <span className="inline-flex items-center gap-1 text-[11px]"><span>Seg:</span> {getExpirationBadge(asset.vigencia_seguro)}</span>}
                              {asset.fecha_verificacion && <span className="inline-flex items-center gap-1 text-[11px]"><span>Ver:</span> {getExpirationBadge(asset.fecha_verificacion)}</span>}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-0.5">
                            <Button size="icon" variant="ghost" title="Historial" onClick={() => handleShowHistorial(asset)} data-testid={`historial-${asset.id}`} className="h-7 w-7">
                              <History className="w-4 h-4 text-slate-500" />
                            </Button>
                            <Button size="icon" variant="ghost" title="Acta de Asignación" onClick={() => handleDownloadActa(asset.id, "asignacion")} data-testid={`acta-asig-${asset.id}`} className="h-7 w-7">
                              <FileDown className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button size="icon" variant="ghost" title="Acta de Recepción/Baja" onClick={() => handleDownloadActa(asset.id, "recepcion")} data-testid={`acta-recep-${asset.id}`} className="h-7 w-7">
                              <FileDown className="w-4 h-4 text-orange-500" />
                            </Button>
                            {canEditModule(user, "activos_fijos") && <Button size="icon" variant="ghost" onClick={() => handleEdit(asset)} data-testid={`edit-asset-${asset.id}`} className="h-7 w-7"><Edit className="w-4 h-4" /></Button>}
                            {canDelete(user) && <Button size="icon" variant="ghost" className="text-red-600 h-7 w-7" onClick={() => handleDelete(asset.id)} data-testid={`delete-asset-${asset.id}`}><Trash2 className="w-4 h-4" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Asset Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>

          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingAsset ? "Editar Activo" : "Nuevo Activo"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Clasificación *</Label>
                  <Select value={formData.clasificacion} onValueChange={(v) => setFormData({ ...formData, clasificacion: v })} disabled={!!editingAsset}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CLASSIFICATIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Estatus</Label>
                  <Select value={formData.estatus} onValueChange={(v) => setFormData({ ...formData, estatus: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ESTATUS_OPTIONS.map(e => <SelectItem key={e} value={e}>{e.charAt(0).toUpperCase() + e.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descripción *</Label>
                <Input required value={formData.descripcion} onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })} />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Marca</Label>
                  {formData.clasificacion === "VEH" && proveedores.length > 0 ? (
                    <Select value={formData.marca || "none"} onValueChange={(v) => setFormData({ ...formData, marca: v === "none" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar marca" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Seleccionar...</SelectItem>
                        {proveedores.map(p => <SelectItem key={p.id} value={p.nombre_comercial}>{p.nombre_comercial}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <Input value={formData.marca} onChange={(e) => setFormData({ ...formData, marca: e.target.value })} />}
                </div>
                <div className="space-y-2">
                  <Label>Modelo</Label>
                  <Input value={formData.modelo} onChange={(e) => setFormData({ ...formData, modelo: e.target.value })} />
                </div>
                {formData.clasificacion === "VEH" && (
                  <div className="space-y-2">
                    <Label>Tipo de Vehículo</Label>
                    <Select value={formData.tipo_vehiculo} onValueChange={(v) => setFormData({ ...formData, tipo_vehiculo: v })}>
                      <SelectTrigger data-testid="tipo-vehiculo-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sedan">Sedan</SelectItem>
                        <SelectItem value="SUV">SUV</SelectItem>
                        <SelectItem value="Pickup">Pickup</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Serie/VIN</Label>
                  <Input value={formData.serie_vin} onChange={(e) => setFormData({ ...formData, serie_vin: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Fecha Compra</Label>
                  <Input type="date" value={formData.fecha_compra} onChange={(e) => setFormData({ ...formData, fecha_compra: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Valor Factura</Label>
                  <Input type="number" min="0" step="0.01" value={formData.valor_factura} onChange={(e) => setFormData({ ...formData, valor_factura: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Proveedor</Label>
                  <Input value={formData.proveedor} onChange={(e) => setFormData({ ...formData, proveedor: e.target.value })} />
                </div>
              </div>

              {formData.clasificacion === "VEH" && (
                <div className="border-t pt-4 space-y-4">
                  <h4 className="font-semibold text-slate-700">Datos del Vehículo</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2"><Label>Placas</Label><Input value={formData.placas} onChange={(e) => setFormData({ ...formData, placas: e.target.value.toUpperCase() })} /></div>
                    <div className="space-y-2">
                      <Label>Entidad</Label>
                      <Select value={formData.entidad} onValueChange={(v) => setFormData({ ...formData, entidad: v })}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent>{ENTIDADES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Vigencia Seguro</Label><Input type="date" value={formData.vigencia_seguro} onChange={(e) => setFormData({ ...formData, vigencia_seguro: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2"><Label>Fecha Verificación</Label><Input type="date" value={formData.fecha_verificacion} onChange={(e) => setFormData({ ...formData, fecha_verificacion: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Año Tenencia</Label><Input value={formData.tenencia_year} onChange={(e) => setFormData({ ...formData, tenencia_year: e.target.value })} /></div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={formData.tenencia_pagada} onChange={(e) => setFormData({ ...formData, tenencia_pagada: e.target.checked })} className="w-4 h-4" />
                        <span>Tenencia Pagada</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Assignment */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-semibold text-slate-700">Asignación / Resguardo</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo de Resguardo</Label>
                    <Select value={formData.tipo_resguardo} onValueChange={(v) => setFormData({ ...formData, tipo_resguardo: v, responsable_id: "" })}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PF">Persona Física (Empleado)</SelectItem>
                        <SelectItem value="PM">Persona Moral (Centro)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Responsable</Label>
                    <Select value={formData.responsable_id} onValueChange={(v) => setFormData({ ...formData, responsable_id: v })}>
                      <SelectTrigger data-testid="responsable-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        {formData.tipo_resguardo === "PF"
                          ? employees.map(e => <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>)
                          : workCenters.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)
                        }
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ubicación</Label>
                    <Select value={formData.ubicacion_resguardo} onValueChange={(v) => setFormData({ ...formData, ubicacion_resguardo: v })}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>{UBICACIONES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Políticas de Asignación */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-slate-700">Políticas de Asignación (Acta PDF)</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-xs text-blue-600"
                    onClick={() => setFormData({ ...formData, politicas_asignacion: DEFAULT_POLITICAS })}
                    data-testid="reset-politicas-btn"
                  >
                    Restaurar por defecto
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Cada línea será un punto numerado en el PDF. Puedes editar, agregar o quitar políticas.</p>
                <textarea
                  className="w-full min-h-[160px] p-3 border rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.politicas_asignacion}
                  onChange={(e) => setFormData({ ...formData, politicas_asignacion: e.target.value })}
                  placeholder="Escribe cada política en una línea separada..."
                  data-testid="politicas-textarea"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{editingAsset ? "Guardar Cambios" : "Crear Activo"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Historial Dialog */}
        <Dialog open={showHistorial} onOpenChange={setShowHistorial}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Historial — {historialAsset?.clave}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-500 -mt-2">{historialAsset?.descripcion} · {historialAsset?.marca} {historialAsset?.modelo}</p>
            {historial.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>Sin movimientos registrados</p>
                <p className="text-xs mt-1">Se registrarán automáticamente al generar actas de asignación o recepción.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {historial.map((h) => (
                  <div key={h.id} className={`flex items-start gap-3 p-3 rounded-lg border ${h.tipo === "asignacion" ? "bg-blue-50/50 border-blue-200" : "bg-orange-50/50 border-orange-200"}`}>
                    <div className={`mt-0.5 p-1.5 rounded-full ${h.tipo === "asignacion" ? "bg-blue-100" : "bg-orange-100"}`}>
                      {h.tipo === "asignacion" ? <FileDown className="w-4 h-4 text-blue-600" /> : <FileDown className="w-4 h-4 text-orange-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className={h.tipo === "asignacion" ? "bg-blue-500" : "bg-orange-500"}>
                          {h.tipo === "asignacion" ? "Asignación" : "Recepción / Baja"}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {new Date(h.fecha).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="text-sm mt-1">
                        <span className="text-slate-500">{h.tipo === "asignacion" ? "Asignado a:" : "Devuelto por:"}</span>{" "}
                        <span className="font-semibold">{h.responsable_nombre || "—"}</span>
                        {h.tipo_resguardo && <span className="text-xs text-slate-400 ml-1">({h.tipo_resguardo === "PF" ? "Persona Física" : "Persona Moral"})</span>}
                      </p>
                      {h.generado_por && <p className="text-xs text-slate-400 mt-0.5">Generado por: {h.generado_por}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default FixedAssets;
